import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { contentService } from "@/services/ContentService";
import type { CaseStudyDto } from "@shared/types/CaseStudyDto";
import type { PackageOfferingDto } from "@shared/types/PackageOfferingDto";

interface ContentContextType {
  caseStudies: CaseStudyDto[];
  packageOfferings: PackageOfferingDto[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const ContentContext = createContext<ContentContextType | null>(null);

export function ContentProvider({ children }: { children: ReactNode }) {
  const [caseStudies, setCaseStudies] = useState<CaseStudyDto[]>([]);
  const [packageOfferings, setPackageOfferings] = useState<
    PackageOfferingDto[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [studies, offerings] = await Promise.all([
        contentService.fetchCaseStudies<CaseStudyDto[]>(),
        contentService.fetchServicePackages<PackageOfferingDto[]>(),
      ]);
      setCaseStudies(studies);
      setPackageOfferings(offerings);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch content",
      );
      console.error("Failed to fetch content:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return (
    <ContentContext.Provider
      value={{
        caseStudies,
        packageOfferings,
        isLoading,
        error,
        refetch: fetchContent,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error("useContent must be used within a ContentProvider");
  }
  return context;
}

export function useCaseStudies() {
  const { caseStudies, isLoading, error, refetch } = useContent();
  return {
    caseStudies,
    isLoading,
    error,
    fetchCaseStudies: refetch,
  };
}

export function usePackageOfferings() {
  const { packageOfferings, isLoading, error, refetch } = useContent();
  return {
    offerings: packageOfferings,
    isLoading,
    error,
    fetchOfferings: refetch,
    getOfferingById: (id: string) =>
      packageOfferings.find((o) => o.id === id),
    getOfferingsByCategory: (category: string) =>
      packageOfferings.filter((o) => o.category === category),
  };
}
